import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardHeader, Button, Collapse, CardBody } from 'reactstrap';
import StatusCode from '../../../../../../util/StatusCode';
import Loading from '../../../../../misc/Loading';
import SelectInput from '../../../../../misc/SelectInput';
import { GetConsumers } from '../../../../../../store/ConsumerAction';
import { MasterFlowSyncConsumer } from '../../../../../../models/MasterFlowSyncConsumer';
import { RequestState } from '../../../../../../models/RequestState';
import { RootState } from '../../../../../../store/store';
import { CorePasswordDistributionApiConfiguration } from '../../../../../../models/CorePasswordDistributionApiConfiguration';
import Copy from '../../../../../misc/Copy';

interface PasswordDistributionApiConfigurationProps {
    disabled: boolean
    configuration: CorePasswordDistributionApiConfiguration
    updatePart: (part: Partial<CorePasswordDistributionApiConfiguration>) => void
}

const PasswordDistributionApiConfiguration = (props: PasswordDistributionApiConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);
    const [selectedConsumer, setSelectedConsumer] = useState<string | undefined>(undefined);
    const dispatch = useDispatch();
    const consumers = useSelector<RootState, RequestState<MasterFlowSyncConsumer[]>>(state => state.consumers.consumers);

    useEffect(() => {
        GetConsumers()(dispatch);
    }, [dispatch]);

    const addConsumer = (selectedConsumer: string | undefined) => {
        if (!selectedConsumer) return;

        let updateConsumers = [...props.configuration.consumers];
        updateConsumers.push(selectedConsumer);

        props.updatePart({ consumers: updateConsumers })
        setSelectedConsumer(undefined);
    }

    const removeConsumer = (selectedConsumer: string) => {
        let configurationConsumers = [...props.configuration.consumers];

        let index = configurationConsumers.indexOf(selectedConsumer);
        if (index > -1) {
            configurationConsumers.splice(index, 1);
        }

        props.updatePart({ consumers: configurationConsumers })
        setSelectedConsumer(undefined);
    }

    const renderConsumers = () => {
        switch (consumers.code) {
            case StatusCode.PENDING: {
                return (<Loading />);
            }
            case StatusCode.ERROR: {
                return (<p>{t('passwordDistributionApi.error', { ns: 'configuration' })}</p>);
            }
            case StatusCode.COMPLETE: {
                if (!consumers.data.length) {
                    return (<p>{t('passwordDistributionApi.noResult', { ns: 'configuration' })}</p>);
                } else {
                    return (
                        <div className="picker-list-component">
                            {props.configuration.consumers.map(c => {
                                const found = consumers.data.find(oc => oc.id === c);
                                const displayName = found?.displayName ?? `${t('passwordDistributionApi.unknown', { ns: 'configuration' })} (${c})`
                                return (
                                    <span key={c} className="picker-list-item list">
                                        <span className="displayName">{displayName}</span>
                                        <button
                                            onClick={() => removeConsumer(c)}
                                            title={t('action.remove', { ns: 'common' })}
                                            className="icon-btn"
                                            disabled={props.disabled}>
                                            <FontAwesomeIcon icon="trash" /></button>
                                    </span>
                                )
                            })}

                            <span className="picker-list-item select">
                                <SelectInput
                                    items={consumers.data
                                        .filter(c => !props.configuration.consumers.some(x => x === c.id))
                                        .map(c => ({ value: c.id, text: c.displayName }))}
                                    onChange={(name, value) => setSelectedConsumer(value)}
                                    name='consumers'
                                    selected={selectedConsumer}
                                    defaultText={t('passwordDistributionApi.addConsumerDefaultSelect', { ns: 'configuration' })}
                                    disabled={props.disabled}
                                    className='picker-list-select'
                                />
                                <Button color="primary"
                                    disabled={!selectedConsumer || props.disabled}
                                    className="btn-primary"
                                    onClick={() => addConsumer(selectedConsumer)}>
                                    {t('action.add', { ns: 'common' })}</Button>
                            </span>
                        </div>
                    );
                }
            }
            default: return;
        }
    }

    const webUrl = `/api/passworddistribution/${props.configuration.moduleId}/{userIdentifier}?role={role}`

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.PasswordDistributionAPI.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>
                        <div className="mb-3">
                            <h5>{t('passwordDistributionApi.api-information', { ns: 'configuration' })}</h5>
                            <div className="mb-1">
                                <strong>{t('passwordDistributionApi.url', { ns: 'configuration' })}</strong>
                                &nbsp;{webUrl}
                                <Copy value={webUrl} />
                            </div>

                            <div className="mb-1">
                                <strong>{t('passwordDistributionApi.parameterDescription', { ns: 'configuration' })}:</strong>
                            </div>
                            <dl>
                                <dt><code>{'{userIdentifier}'}</code></dt>
                                <dd className="mb-1 ml-2">
                                    {t('passwordDistributionApi.userIdentifierDescription', {
                                        ns: 'configuration',
                                    })}
                                </dd>

                                <dt><code>{'{role:Student|Staff}'}</code></dt>
                                <dd className="mb-0 ml-2">
                                    {t('passwordDistributionApi.roleDescription', {
                                        ns: 'configuration',
                                    })}
                                </dd>
                            </dl>
                        </div>

                        <div>
                            <h5>{t('passwordDistributionApi.header', { ns: 'configuration' })}</h5>
                            {renderConsumers()}
                        </div>
                    </div>
                </CardBody>
            </Collapse>
        </Card>
    );
}

export default PasswordDistributionApiConfiguration;
